/**
 * Sourcing workspace + brand-level discovery hooks.
 *
 * Pairs with apps/api/src/routes/sourcing.ts (workspace list) and
 * apps/api/src/routes/brands.ts (POST /:name/discover-suppliers). The
 * returned discoveryRunIds plug into the existing useDiscoveryRun poller
 * in lib/discovery.ts — this module just adds the brand-level entry
 * point and the workspace fetch.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DiscoverSuppliersForBrandResponse, SourcingWorkspaceResponse } from '@sourcito/schemas';
import { apiFetch } from './api';

export type SourcingWorkspaceBrand = SourcingWorkspaceResponse['brands'][number];

export function sourcingWorkspaceQuery() {
  return queryOptions({
    queryKey: ['sourcing', 'workspace'] as const,
    queryFn: () =>
      apiFetch<SourcingWorkspaceResponse>('/api/sourcing/workspace', {
        responseSchema: SourcingWorkspaceResponse,
      }),
    staleTime: 15_000,
  });
}

export function useSourcingWorkspace() {
  return useQuery(sourcingWorkspaceQuery());
}

export function useDiscoverSuppliersForBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (brandName: string) =>
      apiFetch<DiscoverSuppliersForBrandResponse>(
        `/api/brands/${encodeURIComponent(brandName)}/discover-suppliers`,
        {
          method: 'POST',
          body: '{}',
          responseSchema: DiscoverSuppliersForBrandResponse,
        }
      ),
    onSuccess: () => {
      // Workspace counts shift once candidates flow in + get promoted —
      // refresh on the next mount. Don't try to optimistically update;
      // the run IDs returned aren't candidates yet.
      qc.invalidateQueries({ queryKey: ['sourcing', 'workspace'] });
    },
  });
}
